import React, { useState, useEffect, useCallback } from "react";
import { cdnUrl } from "../../../lib/util";
import { Grid, Typography, Button, Box, IconButton } from "@mui/material";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import RepeatIcon from "@mui/icons-material/Repeat";
import httpRequest from "../../../HttpService";
import InfoIcon from "@mui/icons-material/Info";
import CustomTooltip from "./pickUpDeliveryTime/CustomTooltip";
import "./DeliveryTime.css";

const ReturnTripSection = (props) => {
  const [isReturnTripEnabled, setIsReturnTripEnabled] = useState(false);
  const [returnTripOptions, setReturnTripOptions] = useState([]);
  const [selectedReturnOption, setSelectedReturnOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [returnQuoteID, setReturnQuoteID] = useState(null);
  const [error, setError] = useState(null);

  // Check if first leg option is selected
  const isFirstLegSelected = () => {
    return props.info[4]?.timeOption?.quoteTimeOptionID !== undefined;
  };

  // Toggle return trip option
  const handleReturnTripToggle = useCallback(() => {
    // Only allow enabling if there's a first leg option selected
    if (!isReturnTripEnabled && !isFirstLegSelected()) {
      setError("Please select a delivery time option first");
      return;
    }

    const newValue = !isReturnTripEnabled;
    setIsReturnTripEnabled(newValue);

    if (!newValue) {
      resetReturnTripData();
    } else if (isFirstLegSelected()) {
      getReturnQuoteID();
    } else {
      setError("No delivery time option selected");
    }

    props.onChange({ target: "isReturnTripEnabled", value: newValue });
  }, [isReturnTripEnabled, isFirstLegSelected]);

  // Add Ctrl+R hotkey for Return Trip button
  useEffect(() => {
    // Only active when in step 4
    if (props.expanded !== 4) return;

    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'r') {
        event.preventDefault();
        event.stopPropagation();
        handleReturnTripToggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [props.expanded, handleReturnTripToggle]);

  // Reset all return trip related state
  const resetReturnTripData = () => {
    setSelectedReturnOption(null);
    setReturnQuoteID(null);
    setReturnTripOptions([]);
    setError(null);
    props.onChange({ target: "returnTripOption", value: null });
  };

  // Get the return quote ID using the selected first leg time option
  const getReturnQuoteID = () => {
    const timeOptionID = props.info[4]?.timeOption?.quoteTimeOptionID;
    if (!timeOptionID) {
      setError("No time option selected for the first leg");
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    httpRequest.getQuoteReturnID(timeOptionID)
      .then((res) => {
        if (!res) {
          setError("Empty response from server");
          setIsLoading(false);
          return;
        }
        
        if (res.data && (res.data.quoteID || res.data.newQuoteID)) {
          const returnQuoteID = res.data.quoteID || res.data.newQuoteID;
          setReturnQuoteID(returnQuoteID);
          fetchReturnTripOptions(returnQuoteID);
        } else {
          if (typeof res.data === 'string' && res.data.includes('<!doctype html>')) {
            setError("Server returned HTML instead of JSON. API endpoint may not be configured correctly.");
          } else if (res.data === null) {
            setError("Server returned null data");
          } else if (typeof res.data === 'object') {
            setError(`Server response missing quoteID`);
          } else {
            setError("Failed to get return quote ID");
          }
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error getting return quote ID:", error);
        
        let errorMessage = "Failed to get return options";
        if (error.response?.data) {
          errorMessage = typeof error.response.data === 'string' 
            ? error.response.data.substring(0, 100)
            : JSON.stringify(error.response.data).substring(0, 100);
        } else if (error.request) {
          errorMessage = "No response received from server";
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setError(`Error: ${errorMessage}`);
        setIsLoading(false);
      });
  };

  // Fetch return trip options using the return quote ID
  const fetchReturnTripOptions = (quoteID) => {
    if (!quoteID) {
      setError("No quote ID available for return trip");
      setIsLoading(false);
      return;
    }
    
    httpRequest.getQuoteTimeOptions(quoteID)
      .then((res) => {
        if (!res || !res.data) {
          setError("Empty response when fetching return trip options");
          setIsLoading(false);
          return;
        }

        // Handle new response format with timeOptions and editSettings
        const timeOptions = res.data?.timeOptions || res.data;

        if (Array.isArray(timeOptions)) {
          setReturnTripOptions(timeOptions);
        } else {
          if (typeof timeOptions === 'string' && timeOptions.includes('<!doctype html>')) {
            setError("Server returned HTML instead of JSON for return trip options");
          } else if (timeOptions === null) {
            setError("Server returned null data for return trip options");
          } else if (typeof timeOptions === 'object') {
            try {
              const dataArray = Object.values(timeOptions);
              if (dataArray.length > 0) {
                setReturnTripOptions(dataArray);
                setIsLoading(false);
                return;
              }
            } catch (e) {
              console.error("Error converting object to array:", e);
            }
            setError(`Server response for return trip options in unexpected format`);
          } else {
            setError("Return trip options not available in expected format");
          }
          setReturnTripOptions([]);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching return trip options:", error);
        
        let errorMessage = "Failed to fetch return options";
        if (error.response?.data) {
          errorMessage = typeof error.response.data === 'string'
            ? error.response.data.substring(0, 100)
            : JSON.stringify(error.response.data).substring(0, 100);
        } else if (error.request) {
          errorMessage = "No response received from server for return trip options";
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setError(`Error: ${errorMessage}`);
        setReturnTripOptions([]);
        setIsLoading(false);
      });
  };

  // Handle return option selection
  const handleReturnOptionSelect = (index) => (event) => {
    const selectedOption = returnTripOptions.find(e => e.timeOptionID === index);
    props.onChange({ target: "returnTripOption", value: selectedOption });
    setSelectedReturnOption(index);
  };

  // Format pickup time display
  const formatPickupTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  // Effect to update return trip options when first leg option changes
  useEffect(() => {
    const firstLegOption = props.info[4]?.timeOption;
    const firstLegOptionID = firstLegOption?.quoteTimeOptionID;
    
    if (!firstLegOptionID) {
      if (isReturnTripEnabled) {
        setIsReturnTripEnabled(false);
        props.onChange({ target: "isReturnTripEnabled", value: false });
      }
      resetReturnTripData();
      return;
    }
    
    if (isReturnTripEnabled && firstLegOptionID) {
      setSelectedReturnOption(null);
      props.onChange({ target: "returnTripOption", value: null });
      getReturnQuoteID();
    }
  }, [props.info[4]?.timeOption?.quoteTimeOptionID]);

  return (
    <Grid container spacing={2}>
      {/* Return Trip Toggle Button */}
      <Grid item xs={12} style={{ marginTop: 10, marginBottom: 10, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleReturnTripToggle}
          disabled={!isFirstLegSelected() && !isReturnTripEnabled}
          tabIndex={-1}
          style={{
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            padding: '8px 16px',
            backgroundColor: isReturnTripEnabled ? '#337AB7' : '#337AB7',
            color: 'white',
            textTransform: 'uppercase',
            fontWeight: 500,
            fontSize: '14px',
            opacity: (!isFirstLegSelected() && !isReturnTripEnabled) ? 0.6 : 1
          }}
          startIcon={<RepeatIcon />}
        >
          Return Trip (Ctrl + R)
          <CustomTooltip content="Schedule a return trip from the delivery location back to the pickup location">
            <span style={{ display: 'inline-flex', alignItems: 'center' }} tabIndex={-1}>
              <InfoIcon
                color="inherit"
                fontSize="small"
                style={{ marginLeft: 8, pointerEvents: 'none' }}
                tabIndex={-1}
              />
            </span>
          </CustomTooltip>
        </Button>
      </Grid>

      {/* Error Message Display */}
      {error && !isReturnTripEnabled && (
        <Grid item xs={12}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Grid>
      )}

      {/* Return Trip Options Section */}
      {isReturnTripEnabled && (
        <Grid item xs={12} style={{ 
          backgroundColor: '#f5f9ff', 
          padding: '15px', 
          borderRadius: '8px',
          marginTop: '10px',
          border: '1px solid #e0e9f7'
        }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" color="primary" style={{ marginTop: 16 }}>
                Return Trip Options
                {props.info[4]?.timeOption?.dtDeliveryEnd && (
                  <span style={{ fontSize: '14px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                    (Pickup at {formatPickupTime(props.info[4].timeOption.dtDeliveryEnd)})
                  </span>
                )}
              </Typography>
            </Grid>
            
            {/* Loading Skeletons */}
            {isLoading && (
              <div className="delivery-time-cards-container">
                {[1,2,3].map((n) => (
                  <div className="delivery-time-card-wrapper" key={n}>
                    <Skeleton variant="rounded" height={200} style={{ width: '100%', maxWidth: 500 }} />
                  </div>
                ))}
              </div>
            )}

            {/* Error Message */}
            {!isLoading && error && (
              <Grid item xs={12}>
                <Typography variant="body1" color="error" style={{ marginTop: 20, marginBottom: 20 }}>
                  {error}
                </Typography>
              </Grid>
            )}

            {/* Return Trip Options */}
            {!isLoading && !error && (
              <div className="delivery-time-cards-container">
                {returnTripOptions.map((item) => (
                  <div className="delivery-time-card-wrapper" key={item.timeOptionID}>
                    <Button
                      variant="outlined"
                      size="large"
                      disabled={item.optionDisabled}
                      onClick={handleReturnOptionSelect(item.timeOptionID)}
                      className={`delivery-time-option-button delivery-time-card ${selectedReturnOption === item.timeOptionID ? 'selected' : ''}`}
                      tabIndex={props.expanded === 4 && isReturnTripEnabled ? 0 : -1}
                      style={{ backgroundColor: selectedReturnOption === item.timeOptionID ? undefined : 'white' }}
                    >
                      <div className="delivery-time-card-content">
                        <div className="delivery-time-card-header">
                          <div className="delivery-time-card-icon">
                            <img src={cdnUrl(item.optionIconURL)} alt={item.optionText || 'option'} />
                          </div>
                          <div className="delivery-time-card-text">
                            <Typography variant="h6" className="delivery-time-card-title">
                              {item.optionText}
                            </Typography>
                            <Typography variant="body1" className="delivery-time-card-subtext">
                              {item.optionSubText}
                            </Typography>
                            <Typography variant="body1" className="delivery-time-card-line">
                              {item.optionLine1Text}
                            </Typography>
                            <Typography variant="body1" className="delivery-time-card-line">
                              {item.optionLine2Text}
                            </Typography>
                          </div>
                        </div>

                        <Divider />

                        <div className="delivery-time-card-footer">
                          <div className="delivery-time-card-price">
                            {item.showInfo === 1 && (
                              <CustomTooltip content={item.infoText}>
                                <IconButton size="small" className="ml-1" tabIndex={-1}>
                                  <InfoIcon color="primary" fontSize="small" />
                                </IconButton>
                              </CustomTooltip>
                            )}
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'CAD' }).format(item.RATE)}
                          </div>
                          <div className="delivery-time-card-special">
                            {item.optionSpecialText && (
                              <>
                                <img src="https://cdn.directx.ca/icon/warning.png" alt="warning" />
                                <span>{item.optionSpecialText}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* No options message */}
            {!isLoading && !error && returnTripOptions.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body1" style={{ color: "#666", marginTop: 20, marginBottom: 20 }}>
                  No return trip options available for the selected delivery time.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default ReturnTripSection;